import { useQueryClient } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import { useMutation, type MutationResult } from "../../api/use-mutation"
import type { FindingId, ReviewId } from "../../data/review-data"
import type { ClientResolution, Report, ReportView } from "../report-data"

export interface SetClientResolutionVars {
  reviewId: ReviewId
  findingId: FindingId
  /** `null` withdraws the response — un-ticking "reviewed", or withdrawing a dispute. */
  resolution: ClientResolution | null
}

interface Ctx {
  /** This finding's response before the click; `undefined` when the report was not cached. */
  previous: ClientResolution | null | undefined
}

/**
 * Record the client's response to a finding:
 * `PATCH /reviews/{id}/findings/{fid}/client-resolution`.
 *
 * `done` is the checklist's "reviewed" tick and `disputed` its dispute. Applied
 * to the cached report at once and rolled back if the server refuses, so the
 * tick does not lag a round trip behind the click. The rollback restores only
 * this finding: another finding's response may have saved in the meantime.
 */
export function useSetClientResolutionMutation(
  view: ReportView = "client",
): MutationResult<SetClientResolutionVars> {
  const qc = useQueryClient()

  return useMutation<SetClientResolutionVars, Ctx>({
    mutationFn: async ({ reviewId, findingId, resolution }, signal) => {
      await http.patch<unknown, { resolution: ClientResolution | null }>(
        `/api/v1/reviews/${encodeURIComponent(reviewId)}/findings/${encodeURIComponent(findingId)}/client-resolution`,
        { resolution },
        { signal },
      )
      // The workspace shows the client's response too.
      void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
    },
    onMutate: ({ reviewId, findingId, resolution }) => {
      const key = qk.reviews.report(reviewId, view)
      // Cancelled first, so a refetch already in flight cannot land after the
      // optimistic write and put the old tick back. (Cancellation is immediate;
      // the returned promise only reports when it has settled.)
      void qc.cancelQueries({ queryKey: key })
      const report = qc.getQueryData<Report>(key)
      const previous = report?.findings.find((f) => f.id === findingId)?.clientResolution
      qc.setQueryData<Report>(key, (current) => withResolution(current, findingId, resolution))
      return { previous }
    },
    onError: (_error, { reviewId, findingId }, ctx) => {
      if (ctx?.previous === undefined) return
      const previous = ctx.previous
      qc.setQueryData<Report>(qk.reviews.report(reviewId, view), (current) =>
        withResolution(current, findingId, previous),
      )
    },
  })
}

function withResolution(
  report: Report | undefined,
  findingId: FindingId,
  resolution: ClientResolution | null,
): Report | undefined {
  if (!report) return report
  return {
    ...report,
    findings: report.findings.map((f) =>
      f.id === findingId ? { ...f, clientResolution: resolution } : f,
    ),
  }
}
